import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { RecordDetail } from "@/components/RecordDetail";
import { LogCallDialog } from "@/components/LogCallDialog";
import { QualifyingBanks } from "@/components/QualifyingBanks";
import { ForwardLeadDialog, DistributeDialog } from "@/components/LeadActions";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { inr, formatDateTime } from "@/lib/format";
import { Search, ChevronLeft, ChevronRight, PhoneCall, PhoneOutgoing, Share2, Send } from "lucide-react";
import { toast } from "sonner";

const LEAD_STATUSES = ["New", "Contacted", "Not Connected", "Interested", "Not Interested", "Follow-up", "Login", "Rejected"];
const PAGE_SIZE = 25;

function LeadDetailDialog({ lead, open, onOpenChange }) {
  const { data: calls = [] } = useQuery({
    queryKey: ["lead-calls", lead?.id],
    queryFn: async () => (await api.get(`/leads/${lead.id}/calls`)).data,
    enabled: !!lead?.id && open,
  });
  return (
    <RecordDetail open={open} onOpenChange={onOpenChange} title={lead?.name || "Lead"} record={lead}
      extra={
        <>
        <QualifyingBanks lead={lead} />
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1"><PhoneCall className="h-3.5 w-3.5" /> Call History ({calls.length})</div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {calls.length === 0 && <p className="text-sm text-slate-400">No calls logged.</p>}
            {calls.map((c) => (
              <div key={c.id} className="text-sm border border-slate-100 dark:border-slate-800 rounded-lg p-2.5">
                <div className="flex justify-between"><span className="font-medium text-slate-800 dark:text-slate-200">{c.disposition}</span><span className="text-xs text-slate-400 font-num">{formatDateTime(c.called_at)}</span></div>
                <div className="text-xs text-slate-500">{c.user_name}{c.notes ? ` · ${c.notes}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
        </>
      }
    />
  );
}

export default function Leads() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDistribute = ["Admin", "Manager", "Team Leader"].includes(user?.role);
  const [statusFilter, setStatusFilter] = useState("New");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState(null);
  const [callLead, setCallLead] = useState(null);
  const [forwardLead, setForwardLead] = useState(null);
  const [showDistribute, setShowDistribute] = useState(false);

  const { data: stats } = useQuery({ queryKey: ["dashboard-stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", statusFilter],
    queryFn: async () => {
      // 'Contacted' filter is broken upstream (returns 0), so fall back to fetching
      // the recent set and filtering client-side for that one status.
      if (statusFilter === "Contacted") {
        const all = (await api.get("/leads")).data;
        return all.filter((l) => l.status === "Contacted");
      }
      const params = {};
      if (statusFilter !== "all") params.status_filter = statusFilter;
      return (await api.get("/leads", { params })).data;
    },
  });

  const mutate = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/leads/${id}`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); toast.success("Lead updated"); },
    onError: (e) => toast.error(apiError(e)),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => [l.name, l.phone, l.city, l.loan_type, l.assigned_to_name].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [leads, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div data-testid="leads-page">
      <PageHeader title="Leads" subtitle={stats?.total_leads && leads.length >= 500 ? `Showing ${filtered.length.toLocaleString("en-IN")} of ${stats.total_leads.toLocaleString("en-IN")} (most recent)` : `${filtered.length.toLocaleString("en-IN")} leads`}>
        {canDistribute && <Button onClick={() => setShowDistribute(true)} data-testid="distribute-button" className="bg-violet-600 hover:bg-violet-700 text-white"><Share2 className="h-4 w-4 mr-1.5" />Distribute</Button>}
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="leads-search-input" placeholder="Search name, phone, city…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-white dark:bg-slate-950" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-full sm:w-48 bg-white dark:bg-slate-950" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="leads-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Phone</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Loan Type</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Amount</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Assigned To</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 8 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && pageItems.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No leads found.</TableCell></TableRow>}
            {!isLoading && pageItems.map((lead) => (
              <TableRow key={lead.id} className="cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-800/40" data-testid={`lead-row-${lead.id}`} onClick={() => setDetail(lead)}>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{lead.name || "—"}</TableCell>
                <TableCell className="font-num text-slate-600 dark:text-slate-300">{lead.phone || "—"}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{lead.loan_type || "—"}</TableCell>
                <TableCell className="text-right font-num font-semibold">{inr(lead.loan_amount)}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{lead.assigned_to_name || "—"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select value={lead.status || ""} onValueChange={(v) => mutate.mutate({ id: lead.id, body: { status: v } })}>
                    <SelectTrigger className="w-40 h-9 border-slate-200 dark:border-slate-700" data-testid={`lead-status-select-${lead.id}`}><SelectValue>{<StatusBadge status={lead.status} />}</SelectValue></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setCallLead(lead)} data-testid={`lead-log-call-${lead.id}`}>
                      <PhoneOutgoing className="h-3.5 w-3.5 mr-1" /> Log
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Forward / Assign" onClick={() => setForwardLead(lead)} data-testid={`lead-forward-${lead.id}`}>
                      <Send className="h-4 w-4 text-blue-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-500">Page {page + 1} of {pageCount}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="leads-prev-page"><ChevronLeft className="h-4 w-4" /> Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} data-testid="leads-next-page">Next <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {detail && <LeadDetailDialog lead={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />}
      {callLead && <LogCallDialog lead={callLead} open={!!callLead} onOpenChange={(o) => !o && setCallLead(null)} />}
      {forwardLead && <ForwardLeadDialog lead={forwardLead} open={!!forwardLead} onOpenChange={(o) => !o && setForwardLead(null)} />}
      <DistributeDialog open={showDistribute} onOpenChange={setShowDistribute} />
    </div>
  );
}
